import { CardData } from '../types';
import { getCardDimensions } from '../components/Card/cardUtils';

export const SNAP_GUIDE_THRESHOLD = 5;
export const SNAP_ALIGN_COLOR = '#06B6D4';

export type SnapAlignKind =
  | 'left'
  | 'right'
  | 'top'
  | 'bottom'
  | 'center-x'
  | 'center-y';

export interface SnapGuideResult {
  /** Alinhamento magnético ativo entre cards */
  snapActive: boolean;
  /** IDs dos cards de referência envolvidos no alinhamento */
  refCardIds: string[];
  /** Assinatura do alinhamento (para pulse ao engatar) */
  snapSignature: string | null;
  x: number;
  y: number;
}

interface Bounds {
  x: number;
  y: number;
  w: number;
  h: number;
}

interface AxisMatch {
  kind: SnapAlignKind;
  dist: number;
  snapValue: number;
  ref: Bounds;
  refCardId: string;
}

function getBounds(card: CardData, x: number, y: number): Bounds {
  const isDec = card.tipo === 'decisao';
  const dims = getCardDimensions(card, isDec, false);
  return { x, y, w: dims.width, h: dims.height };
}

function cx(b: Bounds): number {
  return b.x + b.w / 2;
}

function cy(b: Bounds): number {
  return b.y + b.h / 2;
}

function pickBestX(drag: Bounds, ref: Bounds, refCardId: string, threshold: number): AxisMatch | null {
  const candidates: AxisMatch[] = [
    { kind: 'left', dist: Math.abs(drag.x - ref.x), snapValue: ref.x, ref, refCardId },
    {
      kind: 'right',
      dist: Math.abs(drag.x + drag.w - (ref.x + ref.w)),
      snapValue: ref.x + ref.w - drag.w,
      ref,
      refCardId,
    },
    {
      kind: 'center-x',
      dist: Math.abs(cx(drag) - cx(ref)),
      snapValue: cx(ref) - drag.w / 2,
      ref,
      refCardId,
    },
  ];
  const valid = candidates.filter(c => c.dist < threshold);
  if (valid.length === 0) return null;
  return valid.reduce((best, c) => (c.dist < best.dist ? c : best));
}

function pickBestY(drag: Bounds, ref: Bounds, refCardId: string, threshold: number): AxisMatch | null {
  const candidates: AxisMatch[] = [
    { kind: 'top', dist: Math.abs(drag.y - ref.y), snapValue: ref.y, ref, refCardId },
    {
      kind: 'bottom',
      dist: Math.abs(drag.y + drag.h - (ref.y + ref.h)),
      snapValue: ref.y + ref.h - drag.h,
      ref,
      refCardId,
    },
    {
      kind: 'center-y',
      dist: Math.abs(cy(drag) - cy(ref)),
      snapValue: cy(ref) - drag.h / 2,
      ref,
      refCardId,
    },
  ];
  const valid = candidates.filter(c => c.dist < threshold);
  if (valid.length === 0) return null;
  return valid.reduce((best, c) => (c.dist < best.dist ? c : best));
}

function buildSnapSignature(matchX?: AxisMatch, matchY?: AxisMatch): string | null {
  const parts: string[] = [];
  if (matchX) parts.push(`${matchX.kind}:${matchX.refCardId}`);
  if (matchY) parts.push(`${matchY.kind}:${matchY.refCardId}`);
  return parts.length > 0 ? parts.join('|') : null;
}

function collectRefCardIds(matchX?: AxisMatch, matchY?: AxisMatch): string[] {
  const ids: string[] = [];
  if (matchX) ids.push(matchX.refCardId);
  if (matchY && matchY.refCardId !== matchX?.refCardId) ids.push(matchY.refCardId);
  return ids;
}

function detectOfficeSnap(
  drag: Bounds,
  cards: CardData[],
  threshold: number,
): { snapX?: number; snapY?: number; matchX?: AxisMatch; matchY?: AxisMatch } {
  let matchX: AxisMatch | undefined;
  let matchY: AxisMatch | undefined;

  for (const card of cards) {
    const ref = getBounds(card, card.posicao.x, card.posicao.y);
    const xCand = pickBestX(drag, ref, card.id, threshold);
    const yCand = pickBestY(drag, ref, card.id, threshold);

    if (xCand && (!matchX || xCand.dist < matchX.dist)) {
      matchX = xCand;
    }
    if (yCand && (!matchY || yCand.dist < matchY.dist)) {
      matchY = yCand;
    }
  }

  return {
    snapX: matchX ? Math.round(matchX.snapValue) : undefined,
    snapY: matchY ? Math.round(matchY.snapValue) : undefined,
    matchX,
    matchY,
  };
}

export function computeSnapGuides(
  draggingCard: CardData,
  newX: number,
  newY: number,
  allCards: CardData[],
  options: {
    mapType: 'OFFICE' | 'FACTORY';
    snapToGridEnabled: boolean;
  },
): SnapGuideResult {
  const FACTORY_Y_SNAP_THRESHOLD = 30;
  const FACTORY_GAP_SNAP_MIN = 18;
  const FACTORY_GAP_SNAP_MAX = 54;
  const FACTORY_GAP_TARGET = 36;

  const cards = allCards.filter(c => c.id !== draggingCard.id);

  let snapX: number | undefined;
  let snapY: number | undefined;
  let matchX: AxisMatch | undefined;
  let matchY: AxisMatch | undefined;

  const drag = getBounds(draggingCard, newX, newY);

  if (options.mapType === 'FACTORY') {
    let bestDist = Infinity;
    for (const card of cards) {
      const ref = getBounds(card, card.posicao.x, card.posicao.y);
      const dist = Math.abs(cy(drag) - cy(ref));
      if (dist < FACTORY_Y_SNAP_THRESHOLD && dist < bestDist) {
        bestDist = dist;
        snapY = Math.round(cy(ref) - drag.h / 2);
        matchY = { kind: 'center-y', dist, snapValue: snapY, ref, refCardId: card.id };
      }
    }

    for (const card of cards) {
      const ref = getBounds(card, card.posicao.x, card.posicao.y);
      const gapLeft = newX - (ref.x + ref.w);
      if (gapLeft >= FACTORY_GAP_SNAP_MIN && gapLeft <= FACTORY_GAP_SNAP_MAX) {
        snapX = ref.x + ref.w + FACTORY_GAP_TARGET;
        matchX = { kind: 'left', dist: 0, snapValue: snapX, ref, refCardId: card.id };
        break;
      }
      const gapRight = ref.x - (newX + drag.w);
      if (gapRight >= FACTORY_GAP_SNAP_MIN && gapRight <= FACTORY_GAP_SNAP_MAX) {
        snapX = ref.x - drag.w - FACTORY_GAP_TARGET;
        matchX = { kind: 'right', dist: 0, snapValue: snapX, ref, refCardId: card.id };
        break;
      }
    }
  } else {
    const office = detectOfficeSnap(drag, cards, SNAP_GUIDE_THRESHOLD);
    snapX = office.snapX;
    snapY = office.snapY;
    matchX = office.matchX;
    matchY = office.matchY;

    if (options.snapToGridEnabled && snapX === undefined) {
      const gridSize = 20;
      const snappedX = Math.round(newX / gridSize) * gridSize;
      if (Math.abs(newX - snappedX) < SNAP_GUIDE_THRESHOLD) {
        snapX = snappedX;
      }
    }

    if (options.snapToGridEnabled && snapY === undefined) {
      const gridSize = 20;
      const snappedY = Math.round(newY / gridSize) * gridSize;
      if (Math.abs(newY - snappedY) < SNAP_GUIDE_THRESHOLD) {
        snapY = snappedY;
      }
    }
  }

  const finalX = snapX ?? newX;
  const finalY = snapY ?? newY;
  const refCardIds = collectRefCardIds(matchX, matchY);
  const snapSignature = buildSnapSignature(matchX, matchY);

  return {
    snapActive: refCardIds.length > 0,
    refCardIds,
    snapSignature,
    x: finalX,
    y: finalY,
  };
}
